import isEqual from 'lodash/isEqual';
import snakeCase from 'lodash/snakeCase';
import toNumber from 'lodash/toNumber';

import { getIso639CodeAndCurrency } from '@/components/Money';

import { getCategoriesFromSlug } from './getCategoriesFromSlug';
import { getUserId } from './getUserId';

declare global {
  interface Window {
    dataLayer: unknown[];
  }
}

const getDiscountAmountAndPercentage = ({
  price,
  originalPrice,
}: {
  price: string;
  originalPrice: string;
}) => {
  const discountAmount = toNumber(originalPrice) - toNumber(price);

  const discountAmountPercentage = Math.round(
    (discountAmount / toNumber(originalPrice)) * 100,
  );

  return {
    // round discountAmount to 2 decimals
    discountAmount: Math.round((discountAmount + Number.EPSILON) * 100) / 100,
    discountAmountPercentage,
  };
};

export enum ProductList {
  CategoryPage = 'Category page',
  CertificationPage = 'Certification page',
  PreOrderPage = 'Pre-order page',
  CrossSell = 'Cross sell',
  DesignerPage = 'Designer page',
  EditorialPage = 'Editorial page',
  RecommendedProducts = 'Recommended products',
  SamplePage = 'Sample page',
  SearchBar = 'Search bar',
  SearchPage = 'Search page',
  TrendingNow = 'Trending now',
  UgcModal = 'UGC modal',
}

export const sendGtmProductAddToCartEvent = ({
  sku,
  productName,
  variantName,
  price,
  originalPrice,
  quantity,
  slug,
  list,
}: {
  sku: string;
  productName: string;
  variantName: string;
  price: string;
  originalPrice: string;
  quantity: number;
  slug: string;
  list?: ProductList;
}) => {
  if (!window.dataLayer) return;

  const item_list_id = list ? snakeCase(list) : undefined;
  const item_list_name = list;

  const { discountAmount, discountAmountPercentage } =
    getDiscountAmountAndPercentage({ price, originalPrice });

  const currency = getIso639CodeAndCurrency().currencyCode;

  const categories = getCategoriesFromSlug(slug)
    .slice(0, -2)
    .map((category, index) => {
      return { [`item_category${index === 0 ? '' : index + 1}`]: category };
    });

  window.dataLayer.push({ ecommerce: null }); // Clear the previous ecommerce object.
  window.dataLayer.push({
    event: 'add_to_cart',
    userId: getUserId(),
    storeCode: process.env.GATSBY_REGION,
    userType: process.env.GATSBY_IS_PRO === 'true' ? 'B2B' : 'B2C',
    ecommerce: {
      currency,
      value: toNumber(price) * quantity,
      item_list_id,
      item_list_name,
      items: [
        Object.assign({}, ...categories, {
          item_id: sku,
          item_name: productName,
          affiliation: process.env.GATSBY_REGION_REGION_DIFFERENTIATOR,
          discount: discountAmount,
          discountPercentage: discountAmountPercentage,
          item_variant: variantName,
          price: toNumber(originalPrice),
          quantity,
        }),
      ],
    },
  });
};

export const sendGtmProductRemoveFromCartEvent = ({
  sku,
  productName,
  variantName,
  price,
  originalPrice,
  quantity,
  slug,
}: {
  sku: string;
  productName: string;
  variantName: string;
  price: string;
  originalPrice: string;
  quantity: number;
  slug: string;
}) => {
  if (!window.dataLayer) return;

  const { discountAmount, discountAmountPercentage } =
    getDiscountAmountAndPercentage({ price, originalPrice });

  const currency = getIso639CodeAndCurrency().currencyCode;

  const categories = getCategoriesFromSlug(slug)
    .slice(0, -2)
    .map((category, index) => {
      return { [`item_category${index === 0 ? '' : index + 1}`]: category };
    });

  window.dataLayer.push({ ecommerce: null }); // Clear the previous ecommerce object.
  window.dataLayer.push({
    event: 'remove_from_cart',
    userId: getUserId(),
    storeCode: process.env.GATSBY_REGION,
    userType: process.env.GATSBY_IS_PRO === 'true' ? 'B2B' : 'B2C',
    ecommerce: {
      currency,
      value: toNumber(price) * quantity,
      items: [
        Object.assign({}, ...categories, {
          item_id: sku,
          item_name: productName,
          affiliation: process.env.GATSBY_REGION_REGION_DIFFERENTIATOR,
          discount: discountAmount,
          discountPercentage: discountAmountPercentage,
          item_variant: variantName,
          price: toNumber(originalPrice),
          quantity,
        }),
      ],
    },
  });
};

let productDetailLastSent: string;
export const sendGtmProductDetailEvent = ({
  sku,
  slug,
  productName,
  variantName,
  price,
  image,
  originalPrice,
}: {
  sku: string;
  slug: string;
  productName: string;
  variantName: string;
  price: string;
  image: string;
  originalPrice: string;
}) => {
  if (!window.dataLayer) return;

  // Don't send the same product detail event twice
  if (productDetailLastSent === sku) {
    return;
  }

  productDetailLastSent = sku;

  const currency = getIso639CodeAndCurrency().currencyCode;
  const { discountAmount, discountAmountPercentage } =
    getDiscountAmountAndPercentage({ price, originalPrice });

  const categories = getCategoriesFromSlug(slug)
    .slice(0, -2)
    .map((category, index) => {
      return { [`item_category${index === 0 ? '' : index + 1}`]: category };
    });

  window.dataLayer.push({ ecommerce: null }); // Clear the previous ecommerce object.
  window.dataLayer.push({
    event: 'view_item',
    ecommerce: {
      currency,
      value: toNumber(price),
      items: [
        Object.assign({}, ...categories, {
          item_id: sku,
          item_name: productName,
          affiliation: process.env.GATSBY_REGION_REGION_DIFFERENTIATOR,
          discount: discountAmount,
          discountPercentage: discountAmountPercentage,
          item_variant: variantName,
          price: toNumber(originalPrice),
          image_url: image ? image + '?w=1200&q=80&fit=pad' : undefined,
        }),
      ],
    },
  });
};

export const sendGtmNewsletterEvent = ({ action }: { action: string }) => {
  if (!window.dataLayer) return;

  window.dataLayer.push({
    event: 'Newsletter',
    userId: getUserId(),
    storeCode: process.env.GATSBY_REGION,
    userType: process.env.GATSBY_IS_PRO === 'true' ? 'B2B' : 'B2C',
    pagePath: document.location.pathname + document.location.search,
    originalLocation: document.location.href,
    eventInfo: {
      category: 'Newsletter',
      action,
      label: document.location.pathname + document.location.search,
    },
  });
};

export const sendGtmSmsEvent = ({ action }: { action: string }) => {
  if (!window.dataLayer) return;

  window.dataLayer.push({
    event: 'SMS',
    userId: getUserId(),
    storeCode: process.env.GATSBY_REGION,
    userType: process.env.GATSBY_IS_PRO === 'true' ? 'B2B' : 'B2C',
    pagePath: document.location.pathname + document.location.search,
    originalLocation: document.location.href,
    eventInfo: {
      category: 'SMS',
      action,
      label: document.location.pathname + document.location.search,
    },
  });
};

export const sendGtmWaitingListSignupEvent = ({ sku }: { sku: string }) => {
  if (!window.dataLayer) return;

  window.dataLayer.push({
    event: 'waitingListSignup',
    userId: getUserId(),
    storeCode: process.env.GATSBY_REGION,
    userType: process.env.GATSBY_IS_PRO === 'true' ? 'B2B' : 'B2C',
    pagePath: document.location.pathname + document.location.search,
    originalLocation: document.location.href,
    productSKU: sku,
  });
};

export const sendGtmSearchBarFocusedEvent = () => {
  if (!window.dataLayer) return;

  window.dataLayer.push({
    event: 'search_bar_focused',
    userId: getUserId(),
    storeCode: process.env.GATSBY_REGION,
    userType: process.env.GATSBY_IS_PRO === 'true' ? 'B2B' : 'B2C',
    pagePath: document.location.pathname + document.location.search,
    originalLocation: document.location.href,
  });
};

type Impression = {
  sku: string;
  productName: string;
  variantName: string;
  price: string;
  originalPrice: string;
  position: number;
  slug: string;
};
let oldImpressions: Impression[] = [];
export const sendGtmProductImpressionsEvent = ({
  list,
  impressions,
}: {
  list: ProductList;
  impressions: Impression[];
}) => {
  if (!window.dataLayer || isEqual(oldImpressions, impressions)) return;

  const currency = getIso639CodeAndCurrency().currencyCode;
  const item_list_id = snakeCase(list);
  const item_list_name = list;
  const userId = getUserId();
  const storeCode = process.env.GATSBY_REGION;
  const userType = process.env.GATSBY_IS_PRO === 'true' ? 'B2B' : 'B2C';
  const items = impressions.map((item) => {
    const { discountAmount, discountAmountPercentage } =
      getDiscountAmountAndPercentage({
        price: item.price,
        originalPrice: item.originalPrice,
      });

    const categories = getCategoriesFromSlug(item.slug)
      .slice(0, -2)
      .map((category, index) => {
        return { [`item_category${index === 0 ? '' : index + 1}`]: category };
      });

    return Object.assign({}, ...categories, {
      item_id: item.sku,
      item_name: item.productName,
      affiliation: process.env.GATSBY_REGION_REGION_DIFFERENTIATOR,
      discount: discountAmount,
      discountPercentage: discountAmountPercentage,
      index: item.position,
      item_list_id,
      item_list_name,
      item_variant: item.variantName,
      price: toNumber(item.originalPrice),
    });
  });

  window.dataLayer.push({ ecommerce: null });
  window.dataLayer.push({
    event: 'view_item_list',
    userId,
    storeCode,
    userType,
    ecommerce: {
      currency,
      item_list_id,
      item_list_name,
      items,
    },
  });

  oldImpressions = impressions;
};

export const sendGtmBeginCheckoutEvent = ({
  value,
  lineItems,
}: {
  value: number;
  lineItems: {
    quantity: number;
    sku: string;
    productName: string;
    variantName: string;
    price: string;
    originalPrice: string;
    slug: string;
  }[];
}) => {
  if (!window.dataLayer) return;

  const currency = getIso639CodeAndCurrency().currencyCode;
  const userId = getUserId();
  const storeCode = process.env.GATSBY_REGION;
  const userType = process.env.GATSBY_IS_PRO === 'true' ? 'B2B' : 'B2C';
  const items = lineItems.map((item, index) => {
    const { discountAmount, discountAmountPercentage } =
      getDiscountAmountAndPercentage({
        price: item.price,
        originalPrice: item.originalPrice,
      });

    const categories = getCategoriesFromSlug(item.slug)
      .slice(0, -2)
      .map((category, index) => {
        return { [`item_category${index === 0 ? '' : index + 1}`]: category };
      });

    return Object.assign({}, ...categories, {
      item_id: item.sku,
      item_name: item.productName,
      affiliation: process.env.GATSBY_REGION_REGION_DIFFERENTIATOR,
      discount: discountAmount,
      discountPercentage: discountAmountPercentage,
      index,
      item_variant: item.variantName,
      price: toNumber(item.originalPrice),
      quantity: item.quantity,
    });
  });

  window.dataLayer.push({ ecommerce: null });
  window.dataLayer.push({
    event: 'begin_checkout',
    userId,
    storeCode,
    userType,
    ecommerce: {
      currency,
      value,
      items,
    },
  });
};

export const sendGtmPurchaseEvent = ({
  userId,
  transaction_id,
  tax,
  value,
  coupon,
  currency,
  lineItems,
  shipping,
}: {
  userId: string;
  transaction_id: string;
  value: number;
  tax: number;
  shipping: number;
  coupon: string;
  currency: string;
  lineItems: {
    quantity: number;
    sku: string;
    productName: string;
    variantName: string;
    price: string;
    originalPrice: string;
    slug: string;
  }[];
}) => {
  if (!window.dataLayer) return;

  const storeCode = process.env.GATSBY_REGION;
  const userType = process.env.GATSBY_IS_PRO === 'true' ? 'B2B' : 'B2C';
  const items = lineItems.map((item, index) => {
    const { discountAmount, discountAmountPercentage } =
      getDiscountAmountAndPercentage({
        price: item.price,
        originalPrice: item.originalPrice,
      });

    const categories = getCategoriesFromSlug(item.slug)
      .slice(0, -2)
      .map((category, index) => {
        return { [`item_category${index === 0 ? '' : index + 1}`]: category };
      });

    return Object.assign({}, ...categories, {
      item_id: item.sku,
      item_name: item.productName,
      affiliation: process.env.GATSBY_REGION_REGION_DIFFERENTIATOR,
      discount: discountAmount,
      discountPercentage: discountAmountPercentage,
      index,
      item_variant: item.variantName,
      price: toNumber(item.originalPrice),
      quantity: item.quantity,
    });
  });

  window.dataLayer.push({ ecommerce: null });
  window.dataLayer.push({
    event: 'purchase',
    userId,
    storeCode,
    userType,
    ecommerce: {
      transaction_id,
      currency,
      value,
      tax,
      shipping,
      coupon,
      items,
    },
  });
};

export const sendGtmProductClickEvent = ({
  sku,
  productName,
  variantName,
  price,
  originalPrice,
  slug,
  list,
  position,
}: {
  sku: string;
  productName: string;
  variantName: string;
  price: string;
  originalPrice: string;
  slug: string;
  list: ProductList;
  position: number;
}) => {
  if (!window.dataLayer) return;

  const currency = getIso639CodeAndCurrency().currencyCode;
  const item_list_id = snakeCase(list);
  const item_list_name = list;
  const userId = getUserId();
  const storeCode = process.env.GATSBY_REGION;
  const userType = process.env.GATSBY_IS_PRO === 'true' ? 'B2B' : 'B2C';
  const { discountAmount, discountAmountPercentage } =
    getDiscountAmountAndPercentage({ price, originalPrice });

  const categories = getCategoriesFromSlug(slug)
    .slice(0, -2)
    .map((category, index) => {
      return { [`item_category${index === 0 ? '' : index + 1}`]: category };
    });

  window.dataLayer.push({ ecommerce: null }); // Clear the previous ecommerce object.
  window.dataLayer.push({
    event: 'select_item',
    userId,
    storeCode,
    userType,
    ecommerce: {
      currency,
      item_list_id,
      item_list_name,
      items: [
        Object.assign({}, ...categories, {
          item_id: sku,
          item_name: productName,
          affiliation: process.env.GATSBY_REGION_REGION_DIFFERENTIATOR,
          discount: discountAmount,
          discountPercentage: discountAmountPercentage,
          index: position,
          item_list_id,
          item_list_name,
          item_variant: variantName,
          price: toNumber(originalPrice),
        }),
      ],
    },
  });
};
